import normalizeCategorySlug from '@/utils/ui/normalizeCategorySlug';
import { isHostEmbed } from '@/utils/environment/surface';

import getPresetSlugFromHostRoute from './hostRoute';
import { getDeeplinkSnapshot } from './snapshot';
import { DeeplinkKind } from './types';

export interface DeeplinkPinTarget {
  /** Category named by the URL (`genaicategory`). */
  categorySlug: string | null;
  /** Preset named by the URL (`presetId`); its section is the category. */
  presetSlug: string | null;
}

/**
 * What a deeplink wants at the top of the library, read from the immutable
 * snapshot so the answer is stable for the whole session.
 *
 * `genaicategory` names the category outright. `presetId` names it indirectly —
 * the category is whichever section holds that preset, which the sections
 * response already tells us (the same derivation `resolvePresetBySlug` makes
 * via `buildPresetFromLibraryItem(item, section.slug)`), so nothing has to wait
 * for the preset request.
 */
export const getDeeplinkPinTarget = (): DeeplinkPinTarget => {
  const deeplink = getDeeplinkSnapshot();

  if (deeplink.kind === DeeplinkKind.Category) {
    return {
      categorySlug: normalizeCategorySlug(deeplink.category) || null,
      presetSlug: null,
    };
  }

  if (deeplink.kind === DeeplinkKind.Preset) {
    return {
      categorySlug: normalizeCategorySlug(deeplink.category ?? '') || null,
      presetSlug: deeplink.presetId || null,
    };
  }

  // Embedded, gen.ai names the preset in its path rather than a `presetId`
  // param — the same deeplink, so its section is pinned the same way.
  if (isHostEmbed()) {
    return { categorySlug: null, presetSlug: getPresetSlugFromHostRoute() };
  }

  return { categorySlug: null, presetSlug: null };
};
